import React, { memo, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform } from 'react-native';
import { AnchoredDropdown } from '@/components/common/admin/desktop/dropdown/AnchoredDropdown';
import { AnchorRect } from '@/hooks/common/useAnchoredPosition';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';

interface DatePickerState {
  tempDate: Date;
  showStartDatePicker: boolean;
  showEndDatePicker: boolean;
  showPublishDatePicker: boolean;
  datePickerMode: 'date' | 'time';
}

interface DateTimePickerModalProps {
  DateTimePicker: any;
  datePickerState: DatePickerState;
  onDateChange: (event: any, selectedDate?: Date) => void;
  onConfirm: (pickerType: 'start' | 'end' | 'publish', selectedDate?: Date) => void;
  onCancel: () => void;
  pickerType: 'start' | 'end' | 'publish';
  title: string;
  onModeChange?: (mode: 'date' | 'time') => void;
  webAnchor?: AnchorRect | null;
  dateOnly?: boolean;
}

interface WebDateInputProps {
  tempDate: Date;
  onDateChange: (event: any, selectedDate?: Date) => void;
  onConfirm: (pickerType: 'start' | 'end' | 'publish', selectedDate?: Date) => void;
  onCancel: () => void;
  pickerType: 'start' | 'end' | 'publish';
  title: string;
  webAnchor?: AnchorRect | null;
  dateOnly: boolean;
}

const WebDateInput: React.FC<WebDateInputProps> = memo(({
  tempDate,
  onDateChange,
  onConfirm,
  onCancel,
  pickerType,
  title,
  webAnchor,
  dateOnly,
}) => {
  const styles = useStyles();
  const { admin } = useAdminColors();

  const toInputValue = (date: Date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    if (dateOnly) {
      return `${y}-${m}-${d}`;
    }
    const hh = String(date.getHours()).padStart(2, '0');
    const mm = String(date.getMinutes()).padStart(2, '0');
    return `${y}-${m}-${d}T${hh}:${mm}`;
  };

  const [value, setValue] = useState<string>(() => toInputValue(tempDate));

  const handleChange = (e: any) => {
    const raw = e.target.value;
    const str = dateOnly ? `${raw}T00:00` : raw;
    const next = new Date(str);
    if (!isNaN(next.getTime())) {
      setValue(raw);
      onDateChange({ type: 'set', nativeEvent: {} } as any, next);
    }
  };

  return (
    <AnchoredDropdown isOpen={true} anchor={webAnchor ?? null} width={320} maxHeight={380}>
      <View style={{ padding: 12 }}>
        <Text style={styles.datePickerTitle}>{title}</Text>
        {/* @ts-ignore - using raw input for web */}
        <input
          type={dateOnly ? 'date' : 'datetime-local'}
          value={value}
          onChange={handleChange}
          style={{
            width: '100%',
            height: 40,
            padding: 8,
            color: admin.formInput.text,
            background: admin.formInput.bg,
            border: `1px solid ${admin.formInput.border}`,
            borderRadius: 8,
            marginTop: 8,
            marginBottom: 12,
          }}
        />
        <View style={{ flexDirection: 'row', justifyContent: 'flex-end', gap: 8 }}>
          <TouchableOpacity onPress={onCancel}>
            <Text style={styles.datePickerButton}>Cancel</Text>
          </TouchableOpacity>
          <TouchableOpacity onPress={() => onConfirm(pickerType)}>
            <Text style={styles.datePickerButton}>Done</Text>
          </TouchableOpacity>
        </View>
      </View>
    </AnchoredDropdown>
  );
});

WebDateInput.displayName = 'WebDateInput';

export const DateTimePickerModal = memo<DateTimePickerModalProps>(({
  DateTimePicker,
  datePickerState,
  onDateChange,
  onConfirm,
  onCancel,
  pickerType,
  title,
  onModeChange,
  webAnchor,
  dateOnly = false,
}) => {
  const styles = useStyles();
  const { admin } = useAdminColors();

  if (!DateTimePicker && Platform.OS !== 'web') {
    return null;
  }

  const isVisible =
    (pickerType === 'start' && datePickerState.showStartDatePicker) ||
    (pickerType === 'end' && datePickerState.showEndDatePicker) ||
    (pickerType === 'publish' && datePickerState.showPublishDatePicker);

  if (!isVisible) {
    return null;
  }

  if (Platform.OS === 'web') {
    return (
      <WebDateInput
        tempDate={datePickerState.tempDate}
        onDateChange={onDateChange}
        onConfirm={onConfirm}
        onCancel={onCancel}
        pickerType={pickerType}
        title={title}
        webAnchor={webAnchor}
        dateOnly={dateOnly}
      />
    );
  }

  if (Platform.OS === 'ios') {
    return (
      <View style={styles.datePickerOverlay}>
        <TouchableOpacity
          style={[StyleSheet.absoluteFillObject, { zIndex: 1 }]}
          onPress={onCancel}
        />
        <View
          style={[
            styles.datePickerModal,
            { zIndex: 2, position: 'absolute', left: 0, right: 0, bottom: 0 },
          ]}
        >
          <View style={styles.datePickerHeader}>
            <TouchableOpacity onPress={onCancel}>
              <Text style={styles.datePickerButton}>Cancel</Text>
            </TouchableOpacity>
            <Text style={styles.datePickerTitle}>{title}</Text>
            <TouchableOpacity onPress={() => onConfirm(pickerType)}>
              <Text style={styles.datePickerButton}>Done</Text>
            </TouchableOpacity>
          </View>
          <View style={styles.datePickerContent}>
            <DateTimePicker
              value={datePickerState.tempDate}
              mode={dateOnly ? 'date' : 'datetime'}
              display="spinner"
              onChange={onDateChange}
              style={styles.dateTimePicker}
              textColor={admin.dataTable.text}
            />
          </View>
        </View>
      </View>
    );
  }

  return (
    <DateTimePicker
      value={datePickerState.tempDate}
      mode={dateOnly ? 'date' : datePickerState.datePickerMode}
      display="default"
      textColor={admin.dataTable.text}
      onChange={(event: any, date?: Date) => {
        if (!date || event.type === 'dismissed') {
          onCancel();
          onModeChange?.('date');
          return;
        }
        onDateChange(event, date);
        if (dateOnly) {
          onConfirm(pickerType, date);
          onModeChange?.('date');
        } else if (onModeChange) {
          if (datePickerState.datePickerMode === 'date') {
            onModeChange('time');
          } else {
            onConfirm(pickerType, date);
            onModeChange('date');
          }
        }
      }}
    />
  );
});

DateTimePickerModal.displayName = 'DateTimePickerModal';

const useStyles = () =>
  useThemedStyles(({ components, semantic }) => {
    const admin = components.admin;
    return StyleSheet.create({
      datePickerModal: {
        backgroundColor: admin.selector.dropdownBg,
        borderTopLeftRadius: 16,
        borderTopRightRadius: 16,
        paddingBottom: 34,
        minHeight: 300,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: -2 },
        shadowOpacity: 0.25,
        shadowRadius: 3.84,
        elevation: 5,
      },
      datePickerHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: 16,
        paddingVertical: 16,
        borderBottomWidth: 1,
        borderBottomColor: admin.dataTable.border,
      },
      datePickerTitle: {
        fontSize: 18,
        fontWeight: '600',
        color: admin.dataTable.text,
      },
      datePickerButton: {
        fontSize: 16,
        fontWeight: '500',
        color: admin.formInput.borderFocused,
      },
      datePickerContent: {
        padding: 16,
      },
      dateTimePicker: {
        height: 200,
      },
      datePickerOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: semantic.background.overlay,
        justifyContent: 'flex-end',
        zIndex: 9999,
      },
    });
  });
